import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { api } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Plus, FolderOpen, AlertCircle, Users, FileText, Image, Loader2, X } from 'lucide-react';
import type { ProjectWithCounts } from '@antree/shared';

interface ProjectsResponse {
  projects: ProjectWithCounts[];
}

interface CreateProjectResponse {
  project: ProjectWithCounts;
}

export function Dashboard() {
  const { user } = useAuth();
  const [projects, setProjects] = useState<ProjectWithCounts[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [creating, setCreating] = useState(false);
  const [newProjectName, setNewProjectName] = useState('');
  const [newProjectDescription, setNewProjectDescription] = useState('');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadProjects();
  }, []);

  async function loadProjects() {
    setLoading(true);
    const response = await api.get<ProjectsResponse>('/projects');
    if (response.success && response.data) {
      setProjects(response.data.projects);
    }
    setLoading(false);
  }

  async function handleCreateProject(e: React.FormEvent) {
    e.preventDefault();
    if (!newProjectName.trim()) return;

    setCreating(true);
    setError(null);

    const response = await api.post<CreateProjectResponse>('/projects', {
      name: newProjectName.trim(),
      description: newProjectDescription.trim() || undefined,
    });

    if (response.success && response.data) {
      setProjects([response.data.project, ...projects]);
      setNewProjectName('');
      setNewProjectDescription('');
      setShowCreateForm(false);
    } else {
      setError(response.error?.message || 'Fehler beim Erstellen des Projekts');
    }
    setCreating(false);
  }

  return (
    <div className="container py-8">
      <div className="flex flex-col gap-6">
        {/* Email Verification Warning */}
        {user && !user.emailVerified && (
          <div className="flex items-center gap-3 p-4 rounded-lg border border-yellow-500/50 bg-yellow-500/10">
            <AlertCircle className="h-5 w-5 text-yellow-600" />
            <div className="flex-1">
              <p className="text-sm font-medium">E-Mail-Adresse nicht bestätigt</p>
              <p className="text-sm text-muted-foreground">
                Bitte bestätige deine E-Mail-Adresse, um alle Funktionen nutzen zu können.
              </p>
            </div>
            <Link to="/verify-email">
              <Button variant="outline" size="sm">
                Bestätigen
              </Button>
            </Link>
          </div>
        )}

        {/* Row 1: Title */}
        <h1 className="text-2xl font-bold tracking-tight">Meine Projekte</h1>

        {/* Row 2: Action Buttons */}
        <div className="flex flex-wrap gap-3">
          {showCreateForm ? (
            <Card className="w-full sm:w-auto sm:min-w-[320px]">
              <CardContent className="pt-4">
                <form onSubmit={handleCreateProject} className="space-y-3">
                  <div className="space-y-1.5">
                    <Label htmlFor="projectName">Projektname</Label>
                    <Input
                      id="projectName"
                      value={newProjectName}
                      onChange={(e) => setNewProjectName(e.target.value)}
                      placeholder="z.B. Familie Müller"
                      required
                      autoFocus
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="projectDescription">Beschreibung (optional)</Label>
                    <Input
                      id="projectDescription"
                      value={newProjectDescription}
                      onChange={(e) => setNewProjectDescription(e.target.value)}
                      placeholder="Kurze Beschreibung..."
                    />
                  </div>
                  {error && (
                    <p className="text-sm text-red-500">{error}</p>
                  )}
                  <div className="flex gap-2">
                    <Button type="submit" disabled={creating} className="flex-1">
                      {creating && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                      Erstellen
                    </Button>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      onClick={() => {
                        setShowCreateForm(false);
                        setNewProjectName('');
                        setNewProjectDescription('');
                        setError(null);
                      }}
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                </form>
              </CardContent>
            </Card>
          ) : (
            <>
              <Button onClick={() => setShowCreateForm(true)}>
                <Plus className="h-4 w-4 mr-2" />
                Neues Projekt
              </Button>
              <Button variant="outline" disabled>
                <FolderOpen className="h-4 w-4 mr-2" />
                GEDCOM importieren
              </Button>
            </>
          )}
        </div>

        {/* Projects List */}
        <div>
          {loading ? (
            <Card>
              <CardContent className="flex items-center justify-center py-12">
                <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
              </CardContent>
            </Card>
          ) : projects.length === 0 ? (
            <Card>
              <CardContent className="flex flex-col items-center justify-center py-12 text-center">
                <FolderOpen className="h-12 w-12 text-muted-foreground mb-4" />
                <h3 className="font-medium">Noch keine Projekte</h3>
                <p className="text-sm text-muted-foreground mt-1">
                  Erstelle dein erstes Projekt oder importiere eine GEDCOM-Datei.
                </p>
              </CardContent>
            </Card>
          ) : (
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
              {projects.map((project) => (
                <Link key={project.id} to={`/projects/${project.id}`}>
                  <Card className="h-full hover:border-primary/50 transition-colors cursor-pointer">
                    <CardHeader>
                      <CardTitle className="text-lg">{project.name}</CardTitle>
                      {project.description && (
                        <CardDescription className="line-clamp-2">
                          {project.description}
                        </CardDescription>
                      )}
                    </CardHeader>
                    <CardContent>
                      <div className="flex gap-4 text-sm text-muted-foreground">
                        <div className="flex items-center gap-1">
                          <Users className="h-4 w-4" />
                          <span>{project._count?.persons || 0}</span>
                        </div>
                        <div className="flex items-center gap-1">
                          <FileText className="h-4 w-4" />
                          <span>{project._count?.documents || 0}</span>
                        </div>
                        <div className="flex items-center gap-1">
                          <Image className="h-4 w-4" />
                          <span>{project._count?.photos || 0}</span>
                        </div>
                      </div>
                      <p className="text-xs text-muted-foreground mt-3">
                        Erstellt am {new Date(project.createdAt).toLocaleDateString('de-DE')}
                      </p>
                    </CardContent>
                  </Card>
                </Link>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
